import type { ReactNode } from "react";
import { Link } from "react-router-dom";

import { Pill } from "../components/Badges";
import { DataTable } from "../components/DataTable";
import { Card, Empty, ErrorBox, Facts, Loading } from "../components/Page";
import { day, money, num, pct } from "../lib/format";
import { useApi } from "../lib/useApi";

interface DocRef { id: number; supplier: string; name: string; kind: string; number: string; date: string | null }
interface Check {
  component: string; product: string; per_bottle: number | null; per_bottle_usd: number | null;
  high_usd?: number | null; sheet: number | null; verdict: string; change: number | null; quote?: boolean;
}
interface SummaryRow extends Check { document: DocRef; currency: string; others: (DocRef & { per_bottle_usd: number | null })[] }
interface DocLine {
  seq: number; item_code: string | null; description: string; qty: number | null; unit: string | null;
  unit_price: number | null; price_per: number | null; amount: number | null; stated_amount: number | null;
  role: string; component: string | null; basis: string | null; products: string[]; note: string | null;
  per_bottle: number | null; per_bottle_usd: number | null;
}
interface SupplierDoc extends DocRef {
  currency: string; currency_per_usd: number | null; rate_source: string | null; terms: string | null; ship_to: string | null;
  stated_total: number | null; computed_total: number | null; adds_up: boolean;
  bottles_produced: number | null; cases_produced: number | null; note: string | null;
  all_in_per_bottle: number | null; all_in_per_bottle_usd: number | null; has_original: boolean;
  lines: DocLine[]; checks: Check[];
}
interface Invoices {
  sheet_as_of: string | null; documents: SupplierDoc[]; summary: SummaryRow[]; currencies_without_rate: string[];
}
interface Vendor {
  supplier: string; category: string | null; contact: string | null; email: string | null;
  party: { id: number; name: string; code: string; kind: string; address: string | null; phone: string | null;
           website: string | null } | null;
  documents: number; latest: DocRef | null; items: number; in_contact_list: boolean;
}

const VERDICT_TONE: Record<string, string> = {
  higher: "warning", lower: "info", matches: "good", "not in sheet": "neutral", "no rate": "neutral", "no figure": "neutral",
};
const ROLE_LABEL: Record<string, string> = { cost: "per bottle", charge: "charge", other: "left out", quote: "quote" };
const ROLE_TONE: Record<string, string> = { cost: "good", charge: "info", other: "neutral", quote: "neutral" };

/** "EUR at 0.8403 to the dollar (€1 = $1.1900)", and where the rate came from. */
function rateText(d: SupplierDoc): ReactNode {
  if (!d.currency_per_usd) return `${d.currency}: no rate to the dollar, so shown in ${d.currency} and not compared`;
  return (
    <>
      {d.currency} at {num(d.currency_per_usd, 4)} to the dollar ({money(1, d.currency, 0)} = {money(1 / d.currency_per_usd, "USD", 4)})
      {d.rate_source ? <div className="muted small">{d.rate_source}</div> : null}
    </>
  );
}

const docName = (d: DocRef) => `${d.name} · ${d.kind.toLowerCase()} ${d.number}`;

function Verdict({ row }: { row: Check }) {
  const word = row.verdict === "higher" || row.verdict === "lower"
    ? `${row.verdict} ${pct(Math.abs(row.change ?? 0), 0)}` : row.verdict;
  return <Pill tone={VERDICT_TONE[row.verdict] ?? "neutral"}>{word}</Pill>;
}

function invoiceFigure(row: Check, currency: string) {
  if (row.quote) {
    return row.per_bottle_usd == null ? "—"
      : <>{money(row.per_bottle_usd, "USD", 4)}–{money(row.high_usd ?? null, "USD", 4)} <span className="muted">estimate</span></>;
  }
  if (row.per_bottle_usd == null) {
    return row.per_bottle == null ? "—" : <>{money(row.per_bottle, currency, 4)} <span className="muted">no rate</span></>;
  }
  return money(row.per_bottle_usd, "USD", 4);
}

/** Every supplier document, worked out per bottle, against the COGS sheet. */
export function InvoiceChecks() {
  const { data, error, reload } = useApi<Invoices>("/costs/invoices");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  if (!data.documents.length) {
    return (
      <Card>
        <Empty>
          No supplier invoices yet. Upload the supplier-invoices workbook on
          the <Link to="/sources">Integrations</Link> page and each part of a bottle is checked
          against your COGS sheet here.
        </Empty>
      </Card>
    );
  }
  const off = data.summary.filter((r) => r.verdict === "higher" || r.verdict === "lower").length;
  return (
    <>
      <Card title="Each part of a bottle: your COGS sheet against the latest invoice">
        <p className="note" style={{ marginTop: 0 }}>
          {data.summary.length} parts checked{data.sheet_as_of ? ` against your COGS sheet of ${day(data.sheet_as_of)}` : ""};
          {" "}{off} differ by more than 2%. A figure counts only what is the material itself — freight,
          tariff and pallets are shown on each document as an all-in figure, and anything you said to
          leave out (extra labour, rush fees) is left out.
          {data.currencies_without_rate.length
            ? ` ${data.currencies_without_rate.join(", ")} documents are shown in their own currency and not compared: no exchange rate has been given for them.`
            : ""}
        </p>
        <DataTable<SummaryRow> testId="invoice-summary" rows={data.summary}
          rowKey={(r) => `${r.component}|${r.product}`} filterPlaceholder="Filter parts…"
          columns={[
            { key: "component", label: "Part" },
            { key: "product", label: "Product" },
            { key: "sheet", label: "COGS sheet", numeric: true, render: (r) => money(r.sheet, "USD", 4) },
            { key: "per_bottle_usd", label: "Invoice", numeric: true, render: (r) => invoiceFigure(r, r.currency) },
            { key: "verdict", label: "", render: (r) => <Verdict row={r} /> },
            { key: "document", label: "From", value: (r) => docName(r.document),
              render: (r) => <a href={`#doc-${r.document.id}`}>{docName(r.document)}</a> },
            { key: "date", label: "Dated", value: (r) => r.document.date, render: (r) => day(r.document.date) },
          ]} />
      </Card>

      <Card title={`The documents (${data.documents.length})`}>
        <div className="supplier-docs" data-testid="supplier-documents">
          {data.documents.map((d) => <SupplierDocument key={d.id} doc={d} />)}
        </div>
      </Card>
    </>
  );
}

function SupplierDocument({ doc }: { doc: SupplierDoc }) {
  const total = doc.stated_total ?? doc.computed_total;
  return (
    <details className="doc-card" id={`doc-${doc.id}`}>
      <summary>
        <strong>{doc.name}</strong> <span className="muted">{doc.kind} {doc.number} · {day(doc.date)}</span>
        <span className="doc-card-total">{total == null ? "quantity breaks" : money(total, doc.currency)}</span>
        {doc.adds_up ? null : <Pill tone="warning">does not add up</Pill>}
      </summary>
      <div className="doc-card-body">
        {doc.note ? <blockquote className="zach-note"><span className="muted">Zach wrote:</span> {doc.note}</blockquote> : null}
        <Facts items={[
          ...(doc.bottles_produced ? [["Bottles it made", `${num(doc.bottles_produced)}${doc.cases_produced ? ` (${num(doc.cases_produced)} cases)` : ""}`] as [string, ReactNode]] : []),
          ...(doc.currency !== "USD" ? [["Currency", rateText(doc)] as [string, ReactNode]] : []),
          ...(doc.all_in_per_bottle != null ? [["All-in per bottle", <>{money(doc.all_in_per_bottle_usd ?? doc.all_in_per_bottle, doc.all_in_per_bottle_usd != null ? "USD" : doc.currency, 4)} <span className="muted">with freight, tariff and pallets</span></>] as [string, ReactNode]] : []),
          ...(doc.stated_total != null && doc.computed_total != null ? [["Lines add up to", `${money(doc.computed_total, doc.currency)} (printed ${money(doc.stated_total, doc.currency)})`] as [string, ReactNode]] : []),
          ...(doc.terms ? [["Terms", doc.terms] as [string, ReactNode]] : []),
          ...(doc.ship_to ? [["Ship to", doc.ship_to] as [string, ReactNode]] : []),
        ]} />
        <div className="table-scroll">
          <table className="grid">
            <thead>
              <tr><th>Line</th><th>Item code</th><th className="n">Qty</th><th className="n">Price</th>
                <th className="n">Amount</th><th>Counts as</th><th className="n">Per bottle</th></tr>
            </thead>
            <tbody>
              {doc.lines.map((ln) => (
                <tr key={ln.seq}>
                  <td>
                    {ln.description}
                    {ln.component ? <div className="muted small">{ln.component}{ln.products.length ? ` · ${ln.products.join(", ")}` : ""}</div> : null}
                    {ln.note ? <div className="muted small">{ln.note}</div> : null}
                  </td>
                  <td>{ln.item_code ? <code>{ln.item_code}</code> : "—"}</td>
                  <td className="n">{num(ln.qty, 3)}{ln.unit ? ` ${ln.unit}` : ""}</td>
                  <td className="n">{ln.unit_price == null ? "—" : money(ln.unit_price, doc.currency, ln.unit_price < 1 ? 4 : 2)}{ln.price_per && ln.price_per !== 1 ? ` / ${num(ln.price_per)}` : ""}</td>
                  <td className="n">{money(ln.amount, doc.currency)}</td>
                  <td><Pill tone={ROLE_TONE[ln.role] ?? "neutral"}>{ROLE_LABEL[ln.role] ?? ln.role}</Pill></td>
                  <td className="n">{ln.per_bottle == null ? "—" : money(ln.per_bottle_usd ?? ln.per_bottle, ln.per_bottle_usd != null ? "USD" : doc.currency, 4)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {doc.checks.length ? (
          <ul className="checks">
            {doc.checks.map((c) => (
              <li key={`${c.component}|${c.product}`}>
                {c.component} for {c.product}: {invoiceFigure(c, doc.currency)} a bottle, sheet {money(c.sheet, "USD", 4)} <Verdict row={c} />
              </li>
            ))}
          </ul>
        ) : null}
        {doc.has_original ? (
          <p className="note"><a href={`/api/costs/supplier-documents/${doc.id}/original`} target="_blank" rel="noreferrer">Open the original</a></p>
        ) : null}
      </div>
    </details>
  );
}

/** Every supplier from the contact list, with the address book's view of them. */
export function Suppliers() {
  const { data, error, reload } = useApi<Vendor[]>("/vendors");
  if (error) return <ErrorBox error={error} onRetry={reload} />;
  if (!data) return <Loading />;
  if (!data.length) return null;
  return (
    <Card title="Suppliers">
      <DataTable<Vendor> testId="suppliers-table" rows={data} rowKey={(r) => r.supplier}
        columns={[
          { key: "category", label: "Buys", render: (r) => r.category ?? "—" },
          { key: "supplier", label: "Supplier",
            render: (r) => (
              <>
                {r.party ? <Link to={`/settings/address-book/${r.party.id}`}>{r.party.name}</Link> : r.supplier}
                {r.party && r.party.name.toLowerCase() !== r.supplier.toLowerCase()
                  ? <div className="muted small">“{r.supplier}” in your contact list</div> : null}
              </>) },
          { key: "contact", label: "Contact", render: (r) => r.contact ?? "—" },
          { key: "email", label: "Email", render: (r) => r.email ?? "—" },
          { key: "address", label: "Address", value: (r) => r.party?.address ?? "",
            render: (r) => (r.party?.address
              ? <span className="pre">{r.party.address}{r.party.phone ? `\n${r.party.phone}` : ""}</span> : "—") },
          { key: "documents", label: "Invoices", numeric: true,
            render: (r) => (r.latest
              ? <a href={`/costs?view=invoices#doc-${r.latest.id}`}>{r.documents} · {day(r.latest.date)}</a>
              : <span className="muted">none yet</span>) },
        ]} />
      <p className="note">
        From the contact list on your COGS workbook. Addresses come from each supplier’s own invoice;
        nothing already in the address book is overwritten.
      </p>
    </Card>
  );
}
